import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../Data/ReduxViewModels/WorksheetEditor';
import deepEquals from '../../../../Lib/Object/deepEquals';
import { Field, NumericInput, TextOutput } from '../../../Core';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import { HorizontalGrid } from '../../../Layout';
import { WorksheetEquipmentDropdown, WorksheetEquipmentStatus, WorksheetGridFieldSection } from '../../Core';

let CompactionDataSection: React.FC<IWorksheetComponent> = ({ namePrefix, createWorksheetSelector }) => {
  const minTestPortions = 4;
  const useMouldForAllTestPortions =
    useSelector(createWorksheetSelector!('Equipment.UseMouldForAllTestPortions')) ?? false;
  const enterConvertedWetDensity =
    useSelector(createWorksheetSelector!('Preparation.EnterConvertedWetDensity')) ?? false;
  const hasMouldWarnings = useSelector(
    pipe(createWorksheetSelector!('TestPortions'), (portions) =>
      portions.some(
        (p: any) => (p.Mould && p.Mould.Status && !p.Mould.Status.InCalibration) || p.MouldCalibrationMismatch
      )
    )
  );
  const testPortionCols = useSelector(
    pipe(createWorksheetSelector!('TestPortions'), (portions) =>
      (portions ?? []).map((p: any, i: number) => ({
        Mould: p.Mould,
        MouldMass: p.MouldMass,
        MouldVolume: p.MouldVolume,
        MouldCalibrationMismatch: p.MouldCalibrationMismatch,
        MouldId: p.Mould?.Id,
        ExcludedPoint: p.ExcludedPoint,
        WetSoilAndMouldMass: p.WetSoilAndMouldMass,
        WetSoilAndContainerMass: p.MoistureDetermination?.DryingTray?.WetSoilAndContainerMass,
        index: i,
      }))
    ),
    deepEquals
  );

  const dispatch = useDispatch();
  const handleGridCreateCol = () =>
    dispatch(
      actions.addArrayEntry({
        name: `${namePrefix}.TestPortions`,
        index: testPortionCols.length,
        data: undefined,
        sync: true,
      })
    );
  const handleGridRemoveCol = (index: number) =>
    dispatch(actions.removeArrayEntry({ name: `${namePrefix}.TestPortions`, index, sync: true }));
  const confirmPortionRemove = ({ name }: any) => {
    const {
      WetSoilAndMouldMass: { value: wetSoilAndMouldMass } = { value: undefined },
      WetSoilAndContainerMass: { value: wetSoilAndContainerMass } = { value: undefined },
    } = testPortionCols[name];

    return !!(wetSoilAndMouldMass || wetSoilAndContainerMass);
  };

  const compactionHiddenRows = () => {
    let hiddenRows = [];

    // Hide mould status row if no warnings
    if (useMouldForAllTestPortions || !hasMouldWarnings) {
      hiddenRows.push(0);
    }

    // Hide water added fields used to calculate CWD
    // 4: PortionMass
    // 5: WaterAddedTargetPercent
    // 6: WaterAddedTarget
    // 7: WaterAdded
    // 8: WaterAddedPercent
    if (!enterConvertedWetDensity) {
      hiddenRows.push(4, 5, 6, 7, 8);
    }

    return hiddenRows.length > 0 ? hiddenRows : undefined;
  };

  const compactionDataData = (i: number) => [
    <WorksheetEquipmentStatus
      equipmentType="density-mould"
      value={testPortionCols[i].Mould}
      warningIconOnly={true}
      importedCalibration={{
        Mass: testPortionCols[i].MouldMass,
        Volume: testPortionCols[i].MouldVolume,
        Mismatch: testPortionCols[i].MouldCalibrationMismatch,
      }}
      importModel={{
        Id: testPortionCols[i].MouldId,
      }}
    />,
    <Field
      input={<WorksheetEquipmentDropdown />}
      equipmentType="density-mould"
      placeholder="Select..."
      name={`TestPortions[${i}].Mould`}
      label="Mold"
      disabled={useMouldForAllTestPortions}
    />,
    <Field
      input={
        useMouldForAllTestPortions || (testPortionCols[i].Mould && testPortionCols[i].Mould.Mass) ? (
          <TextOutput />
        ) : (
          <NumericInput />
        )
      }
      name={`TestPortions[${i}].MouldMass`}
      label="Mass"
    />,
    <Field
      input={
        useMouldForAllTestPortions || (testPortionCols[i].Mould && testPortionCols[i].Mould.Volume) ? (
          <TextOutput />
        ) : (
          <NumericInput />
        )
      }
      name={`TestPortions[${i}].MouldVolume`}
      label="Volume"
    />,
    <Field input={<NumericInput />} name={`TestPortions[${i}].PortionMass`} label="Portion Mass" />,
    <Field
      input={<NumericInput />}
      name={`TestPortions[${i}].WaterAddedTargetPercent`}
      label="Water Added Target (%)"
    />,
    <Field input={<TextOutput />} name={`TestPortions[${i}].WaterAddedTarget`} label="Water to Add" />,
    <Field input={<NumericInput />} name={`TestPortions[${i}].WaterAdded`} label="Water Added" />,
    <Field input={<TextOutput />} name={`TestPortions[${i}].WaterAddedPercent`} label="Actual Water Added (%)" />,
    <Field input={<NumericInput />} name={`TestPortions[${i}].WetSoilAndMouldMass`} label="Wet Soil and Mold Mass" />,
    <Field input={<TextOutput />} name={`TestPortions[${i}].WetSoilMass`} label="Wet Soil Mass" />,
    <Field input={<TextOutput />} name={`TestPortions[${i}].WetDensity`} label="Wet Density" />,
  ];

  return (
    <>
      <WorksheetGridFieldSection name="TestPortions">
        <HorizontalGrid
          name="grid.CompactionData"
          cornerHeader="Test Portion ID"
          rowHeaderWidth={180}
          colHeaders={testPortionCols.map((_: any, i: number) => i + 1)}
          data={testPortionCols.map((_: any, i: number) => compactionDataData(i))}
          columns={testPortionCols.map((tp: any) => ({ dim: tp.ExcludedPoint }))}
          colWidths={130}
          onCreateCol={handleGridCreateCol}
          scrollLinkId="mddCompaction"
          onRemoveCol={testPortionCols.length > (minTestPortions ?? 4) ? handleGridRemoveCol : undefined}
          removeConfirm={{
            confirm: confirmPortionRemove,
            confirmTitle: 'Portion Deletion',
            confirmText: 'Are you sure you want to delete the selected portion?',
            confirmButtonText: 'Delete Portion',
          }}
          hiddenRows={compactionHiddenRows()}
          rowHeaders={[
            '',
            'Mold',
            `Mass (g)`,
            `Volume (cm³)`,
            `Portion Mass (g)`,
            'Water Added Target (%)',
            `Water to Add (g)`,
            `Water Added (g)`,
            'Actual Water Added (%)',
            `Wet Soil and Mold Mass (g)`,
            `Wet Soil Mass (g)`,
            `Wet Density (kg/m³)`,
          ]}
        />
      </WorksheetGridFieldSection>
    </>
  );
};

CompactionDataSection = withInjectedProps(CompactionDataSection);

export { CompactionDataSection };
export default CompactionDataSection;
